import { useCallback, useState } from 'react';
import { generateSampleGenome } from '../lib/sample';
import { SUPERPOPS, SUPERPOP_LABELS, type Superpop } from '../lib/frequencies';
import type { ParsedGenome } from '../types';
import { Explain } from './Explain';

interface Props {
  onLoaded: (genome: ParsedGenome) => void;
}

/**
 * The pocket edition's landing card. Replaces FileLoader entirely — and that
 * replacement is the edition's whole point. There is no dropzone, no
 * <input type="file">, and no parser in this bundle: the ingest path is not
 * hidden behind a flag, it was never compiled in. A link to this edition is
 * safe to hand to anyone because there is nothing here to phish.
 */
export function PocketLoader({ onLoaded }: Props) {
  const [busy, setBusy] = useState(false);

  const loadSample = useCallback((pop: Superpop) => {
    setBusy(true);
    // Defer so the button paints its busy state before the synchronous build.
    setTimeout(() => {
      const genome = generateSampleGenome(pop);
      genome.meta.parsedAt = Date.now();
      onLoaded(genome);
      setBusy(false);
    }, 0);
  }, [onLoaded]);

  return (
    <div className="card">
      <div className="pocket-hero">
        <h3>
          Pocket edition — explore a genome without bringing your own
          <Explain id="pocket" label="Why can't this read files?" />
        </h3>
        <p>
          Pick an ancestry below to open a fully synthetic genome, drawn from real
          1000&nbsp;Genomes population frequencies. Everything works — conditions,
          the chromosome map, the 3D nucleus, the explainers — but it is nobody's
          data, and{' '}
          <strong>this edition has no way to read a file, by design</strong>.
        </p>
      </div>
      <div className="loader-actions">
        <div className="sample-picker">
          <span className="sample-label">Choose a sample genome</span>
          <div className="sample-pops">
            {SUPERPOPS.map((p) => (
              <button
                key={p}
                className="ghost sample-btn"
                onClick={() => loadSample(p)}
                disabled={busy}
                title={`Synthetic genome drawn from 1000 Genomes ${SUPERPOP_LABELS[p]} allele frequencies`}
              >
                {busy ? '…' : SUPERPOP_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="note info" style={{ marginTop: 16 }}>
        Have your own genome file? Don't load it into links people send you —
        including this one, which is why it won't let you. Use the full edition
        on your own machine, where the code is yours to inspect and the strict
        no-network policy protects real data.
      </div>
    </div>
  );
}
